import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Award, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import type { Kibuddim, KibuddimSection as KSection, KibuddimRole } from '@peles/shared';
import {
  useKibuddimList,
  useCreateKibuddim,
  useDeleteKibuddim,
  useAddKibuddimSection,
  useDeleteKibuddimSection,
  useAddKibuddimRole,
  useUpdateKibuddimRole,
  useDeleteKibuddimRole,
} from '../hooks/useEventOps';
import { SaveAsTemplateButton } from '../components/event-ops/SaveAsTemplateButton';
import { useDialog } from '../context/DialogContext';

export function KibuddimPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { confirm } = useDialog();
  const { data: kibuddimList = [], isLoading } = useKibuddimList(eventId);
  const createKibuddim = useCreateKibuddim();
  const deleteKibuddim = useDeleteKibuddim();
  const addSection = useAddKibuddimSection();
  const deleteSection = useDeleteKibuddimSection();
  const addRole = useAddKibuddimRole();
  const updateRole = useUpdateKibuddimRole();
  const deleteRole = useDeleteKibuddimRole();

  const [expandedKibuddim, setExpandedKibuddim] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [newSectionName, setNewSectionName] = useState('');
  const [addingRole, setAddingRole] = useState<string | null>(null);
  const [roleForm, setRoleForm] = useState({ roleName: '', roleNameEn: '', assignee: '' });

  const isEventScoped = !!eventId;

  const handleCreateKibuddim = useCallback(() => {
    createKibuddim.mutate({ eventId, title: 'New Kibuddim List' });
  }, [createKibuddim, eventId]);

  const handleDeleteKibuddim = useCallback(
    async (id: string) => {
      if (
        await confirm({
          title: 'Delete Kibuddim List',
          message: 'Delete this kibuddim list?',
          confirmLabel: 'Delete',
        })
      ) {
        deleteKibuddim.mutate(id);
      }
    },
    [deleteKibuddim, confirm],
  );

  const toggleKibuddim = useCallback((id: string) => {
    setExpandedKibuddim((prev) => (prev === id ? null : id));
  }, []);

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddSection = useCallback(
    (kibuddimId: string) => {
      if (!newSectionName.trim()) return;
      addSection.mutate({ kibuddimId, name: newSectionName.trim() });
      setNewSectionName('');
    },
    [addSection, newSectionName],
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      deleteSection.mutate(sectionId);
    },
    [deleteSection],
  );

  const handleAddRole = useCallback(
    (sectionId: string) => {
      if (!roleForm.roleName.trim()) return;
      addRole.mutate({
        sectionId,
        roleName: roleForm.roleName.trim(),
        roleNameEn: roleForm.roleNameEn || undefined,
        assignee: roleForm.assignee || undefined,
      });
      setRoleForm({ roleName: '', roleNameEn: '', assignee: '' });
      setAddingRole(null);
    },
    [addRole, roleForm],
  );

  const handleUpdateAssignee = useCallback(
    (roleId: string, assignee: string) => {
      updateRole.mutate({ roleId, updates: { assignee: assignee || null } });
    },
    [updateRole],
  );

  const handleDeleteRole = useCallback(
    (roleId: string) => {
      deleteRole.mutate(roleId);
    },
    [deleteRole],
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading kibuddim...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEventScoped ? 'Event Kibuddim' : 'All Kibuddim'}
        </h1>
        <button
          onClick={handleCreateKibuddim}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">New Kibuddim</span>
        </button>
      </div>

      {kibuddimList.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Award className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">No kibuddim lists yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {kibuddimList.map((kibuddim: Kibuddim) => (
            <div
              key={kibuddim.id}
              className="bg-white rounded-lg shadow"
              data-testid={`kibuddim-${kibuddim.id}`}
            >
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleKibuddim(kibuddim.id)}
              >
                <div className="flex items-center gap-2">
                  {expandedKibuddim === kibuddim.id ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                  <Award size={18} className="text-amber-600" />
                  <span className="font-medium">{kibuddim.title || 'Untitled Kibuddim'}</span>
                  <span className="text-xs text-gray-400">
                    ({kibuddim.sections.length} sections)
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {!kibuddim.isTemplate && (
                    <SaveAsTemplateButton
                      id={kibuddim.id}
                      type="kibuddim"
                      defaultName={`${kibuddim.title || 'Kibuddim'} Template`}
                    />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteKibuddim(kibuddim.id);
                    }}
                    className="text-gray-400 hover:text-red-600 p-1"
                    aria-label="Delete kibuddim"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {expandedKibuddim === kibuddim.id && (
                <div className="border-t border-gray-200 px-4 py-4">
                  {kibuddim.sections.map((section: KSection) => (
                    <div
                      key={section.id}
                      className="mb-3 border border-gray-200 rounded-md"
                      data-testid={`section-${section.id}`}
                    >
                      <div
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer"
                        onClick={() => toggleSection(section.id)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedSections.has(section.id) ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronRight size={14} />
                          )}
                          <span className="text-sm font-medium">{section.name}</span>
                          {section.nameHe && (
                            <span className="text-xs text-gray-400 font-hebrew">
                              {section.nameHe}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(section.id);
                          }}
                          className="text-gray-400 hover:text-red-600 p-0.5"
                          aria-label="Delete section"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {expandedSections.has(section.id) && (
                        <div className="px-3 py-2">
                          <div className="space-y-2">
                            {section.roles.map((role: KibuddimRole) => (
                              <div
                                key={role.id}
                                className="flex items-center gap-3 py-1 border-b border-gray-100 last:border-0"
                                data-testid={`role-${role.id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium" dir="rtl">
                                    {role.roleName}
                                  </span>
                                  {role.roleNameEn && (
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({role.roleNameEn})
                                    </span>
                                  )}
                                </div>
                                <input
                                  type="text"
                                  value={role.assignee || ''}
                                  onChange={(e) => handleUpdateAssignee(role.id, e.target.value)}
                                  placeholder="Assignee"
                                  className="w-40 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                                <button
                                  onClick={() => handleDeleteRole(role.id)}
                                  className="text-gray-400 hover:text-red-600 p-0.5 shrink-0"
                                  aria-label="Delete role"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ))}
                          </div>

                          {addingRole === section.id ? (
                            <div className="mt-2 space-y-2" data-testid="add-role-form">
                              <input
                                type="text"
                                value={roleForm.roleName}
                                onChange={(e) =>
                                  setRoleForm((f) => ({ ...f, roleName: e.target.value }))
                                }
                                placeholder="Role name (Hebrew)"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                dir="rtl"
                              />
                              <input
                                type="text"
                                value={roleForm.roleNameEn}
                                onChange={(e) =>
                                  setRoleForm((f) => ({ ...f, roleNameEn: e.target.value }))
                                }
                                placeholder="Role name (English)"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="text"
                                value={roleForm.assignee}
                                onChange={(e) =>
                                  setRoleForm((f) => ({ ...f, assignee: e.target.value }))
                                }
                                placeholder="Assignee"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAddRole(section.id)}
                                  className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => setAddingRole(null)}
                                  className="px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setAddingRole(section.id)}
                              className="mt-2 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                            >
                              <Plus size={12} /> Add Role
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add Section */}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      placeholder="New section name"
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <button
                      onClick={() => handleAddSection(kibuddim.id)}
                      disabled={!newSectionName.trim()}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 disabled:opacity-50"
                    >
                      Add Section
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
